import { Fragment, type ReactNode } from 'react';

/**
 * ============================================================================
 * THE MARKDOWN RENDERER — and it is a SECURITY component, not a formatting one
 * ============================================================================
 *
 * It renders the operator-authored body of a §2.5a payment-instruction block: text
 * written by STAFF in the administrative interface and displayed in a SUBSCRIBER's
 * browser. That is the one boundary this system otherwise keeps absolute — staff and
 * subscribers share no session store, no audience and no listener — so a body rendered
 * as markup would make a compromised staff account a stored-XSS vector across it.
 *
 * # Two rules, and both are STRUCTURAL rather than filtered
 *
 * A sanitizer is a list of things to remove, and a list is a thing somebody widens. This
 * renderer instead cannot express the dangerous shapes at all:
 *
 *  1. **NO HTML STRING IS EVER BUILT.** There is no `dangerouslySetInnerHTML` anywhere in
 *     this app (`Markdown.test.tsx` sweeps `src/` for the identifier). Every output is a
 *     React ELEMENT, and React escapes text children by construction — so
 *     `<script>alert(1)</script>` in a body becomes those twenty-four characters on the
 *     page. There is nothing to sanitize because nothing is parsed as markup.
 *  2. **THERE IS NO IMAGE NODE, NO EMBED AND NO STYLE.** `![alt](url)` is not a
 *     supported construct and renders as its own literal text. This is the walled-garden
 *     rule made unbreakable: a financially suspended subscriber is exactly the reader
 *     this page exists for, and the garden's allow-list covers the cabinet and the
 *     learned acquirer domains and NOTHING else — so a remote image renders his only
 *     written route to paying us as a broken box, with no diagnostic, for exactly the
 *     population it was written for. `apps/portal/src/bundle.test.ts` holds that rule for
 *     the BUNDLE; a Markdown renderer with an `<img>` in it would defeat that guard at
 *     runtime with operator content the guard never sees.
 *
 * A LINK is the one outward-pointing construct that survives, and it survives because a
 * link is FOLLOWED BY A DELIBERATE ACT and fetches nothing on its own. Its href is
 * restricted to `https:`, `http:`, `mailto:`, `tel:` and same-origin paths — a
 * `javascript:` or `data:` href renders as plain text, not as a dead anchor, so nothing
 * on the page invites a press that would do nothing.
 *
 * # The subset, and why it is this subset
 *
 * Appendix L §2.5a: «Markdown gives the operator headings, lists, bold and links, which
 * is the whole of what an instruction page needs». Precisely that is implemented —
 * headings, paragraphs, unordered and ordered lists, blockquotes, bold, italic, inline code
 * and links — and nothing else. Anything unrecognised is TEXT, which is the honest failure
 * mode: an operator who writes a table sees his table's characters, not a blank.
 *
 * ⚠ THE BLOCKQUOTE IS HERE BECAUSE RUNNING THE PRODUCT FOUND IT, and it is worth recording
 * how. The subset above was written from §2.5a's own sentence, which does not mention
 * blockquotes — and then the demo instance was opened, and **both seeded blocks begin with
 * one**: «> **ЗАПОЛНИТЕ ЭТОТ ТЕКСТ.** …», the placeholder banner every installation ships
 * with until an operator replaces it. Without this branch the first thing a subscriber read
 * on that page was a stray «>» in the middle of a sentence. Nothing in the test suite could
 * have caught it: the fixtures were written from the same sentence as the renderer.
 *
 * The server refuses raw HTML at SAVE with the offending fragment named, so this
 * renderer's HTML-as-text behaviour is a second door on the same room. Both doors,
 * deliberately: «one door of a pair guarded and its neighbour not» is this project's
 * single most repeated review finding, and here the neighbour is a body that predates
 * the validator or arrives through a migration.
 */

/** The href schemes a rendered link may carry. Anything else is not a link at all. */
const SAFE_LINK = /^(?:https?:\/\/|mailto:|tel:|\/)/i;

export function Markdown({ text }: { text: string }) {
  return <div className="pa-md">{blocksOf(text)}</div>;
}

/**
 * blocksOf splits the body into block elements.
 *
 * Line-oriented rather than a real parser: the grammar above has no nesting, and a
 * hand-rolled recursive parser for an operator's «Оплата в офисе» paragraph would be more
 * code to be wrong in than the feature is worth.
 */
function blocksOf(text: string): ReactNode[] {
  const lines = String(text ?? '').replace(/\r\n?/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let paragraph: string[] = [];
  let quote: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    out.push(<p key={`p${out.length}`}>{inline(paragraph.join(' '))}</p>);
    paragraph = [];
  };
  const flushQuote = () => {
    if (quote.length === 0) return;
    out.push(<blockquote key={`q${out.length}`}>{inline(quote.join(' '))}</blockquote>);
    quote = [];
  };
  const flushList = () => {
    if (!list) return;
    const items = list.items.map((item, i) => <li key={i}>{inline(item)}</li>);
    out.push(
      list.ordered ? <ol key={`l${out.length}`}>{items}</ol> : <ul key={`l${out.length}`}>{items}</ul>,
    );
    list = null;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.trim() === '') {
      flushParagraph();
      flushQuote();
      flushList();
      continue;
    }

    // A BLOCKQUOTE, which is what both seeded instruction blocks open with. Consecutive
    // `>` lines are one quote; the marker is stripped and the rest goes through `inline`,
    // so the «**ЗАПОЛНИТЕ ЭТОТ ТЕКСТ.**» inside it is still bold.
    const quoted = /^>\s?(.*)$/.exec(line.trim());
    if (quoted) {
      flushParagraph();
      flushList();
      quote.push(quoted[1]);
      continue;
    }
    flushQuote();

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      // ⚠ h3 AND BELOW ONLY. The page's own «Другие способы оплаты» is the h1 and the
      // block's `title` is the h2, so an operator's own «#» must not outrank either — a
      // document with two h1s is one whose outline lies to a screen reader.
      const level = Math.min(heading[1].length, 4);
      const Tag = (level <= 1 ? 'h3' : level === 2 ? 'h4' : level === 3 ? 'h5' : 'h6') as
        | 'h3'
        | 'h4'
        | 'h5'
        | 'h6';
      out.push(<Tag key={`h${out.length}`}>{inline(heading[2])}</Tag>);
      continue;
    }

    const bullet = /^[-*]\s+(.*)$/.exec(line.trim());
    if (bullet) {
      flushParagraph();
      if (!list || list.ordered) {
        flushList();
        list = { ordered: false, items: [] };
      }
      list.items.push(bullet[1]);
      continue;
    }

    const numbered = /^\d+[.)]\s+(.*)$/.exec(line.trim());
    if (numbered) {
      flushParagraph();
      if (!list || !list.ordered) {
        flushList();
        list = { ordered: true, items: [] };
      }
      list.items.push(numbered[1]);
      continue;
    }

    flushList();
    paragraph.push(line.trim());
  }
  flushParagraph();
  flushQuote();
  flushList();
  return out;
}

/**
 * inline renders the span-level grammar: `**bold**`, `*italic*`, `` `code` `` and
 * `[text](href)`.
 *
 * Everything it does not recognise — including `![alt](url)`, an HTML tag, an unclosed
 * marker — falls through as TEXT, which React escapes. There is deliberately no «repair»
 * branch: a renderer that guessed at a malformed construct would be a renderer whose
 * output an author cannot predict.
 */
function inline(text: string): ReactNode {
  const nodes: ReactNode[] = [];
  // ⚠ THE LINK BRANCH MATCHES AN OPTIONAL LEADING `!`, and that is the whole of the
  // no-external-asset rule at the span level: `![alt](url)` is IMAGE syntax, it is matched
  // here so that it cannot fall through to the link branch as `[alt](url)`, and `link()`
  // renders it as its own literal text. Without the `!` in this pattern an image would
  // become an ANCHOR to the image URL — not a fetch, so the garden rule would survive, but
  // a construct nobody wrote and nobody can predict.
  const pattern = /(\*\*[^*]+\*\*)|(\*[^*]+\*)|(`[^`]+`)|(!?\[[^\]\n]*\]\([^)\s]+\))/g;
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(pattern)) {
    const at = m.index ?? 0;
    if (at > last) nodes.push(<Fragment key={key++}>{text.slice(last, at)}</Fragment>);
    const token = m[0];
    if (token.startsWith('**')) {
      nodes.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      nodes.push(
        <code className="mono" key={key++}>
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('[') || token.startsWith('!')) {
      nodes.push(link(token, key++));
    } else {
      nodes.push(<em key={key++}>{token.slice(1, -1)}</em>);
    }
    last = at + token.length;
  }
  if (last < text.length) nodes.push(<Fragment key={key++}>{text.slice(last)}</Fragment>);
  return nodes;
}

/**
 * link renders `[text](href)` — or, when the token is an IMAGE or the href is not one this
 * renderer will emit, the ORIGINAL MARKUP as text.
 *
 * ⚠ `![alt](url)` ALWAYS FALLS THROUGH TO TEXT. There is no image node in this renderer and
 * there must not be one: the reader this page exists for is inside the walled garden, whose
 * allow-list covers this cabinet and the learned acquirer domains and nothing else, so a
 * remote image is a broken box with no diagnostic for exactly the population the page was
 * written for. `bundle.test.ts` cannot see operator content typed tomorrow; this line is
 * what holds the rule at runtime, and `InstructionsScreen.test.tsx` cuts it.
 *
 * Rendering the raw token rather than the label alone is deliberate: an operator who pastes
 * an image or a `javascript:` href must be able to see what he wrote and fix it, and a
 * subscriber must not be shown a piece of text that was silently stripped of its link.
 */
function link(token: string, key: number): ReactNode {
  const m = /^\[([^\]\n]*)\]\(([^)\s]+)\)$/.exec(token);
  if (!m || !SAFE_LINK.test(m[2])) return <Fragment key={key}>{token}</Fragment>;
  return (
    // `rel` is set because `target="_blank"` without `noopener` hands the opened page a
    // handle on this one, and this one is a session-bearing cabinet.
    <a key={key} href={m[2]} target="_blank" rel="noopener noreferrer nofollow">
      {m[1] || m[2]}
    </a>
  );
}
